import * as React from "preact";
import { Fragment, FunctionComponent } from "preact";

import { fundEventTypes, pageTypes } from "../../../../helpers/constants";
import MetaItem from "../MetaItem";
import { IResult } from "../Result";
import CompleteOnline from "../CompleteOnline";
import cx from "classnames";

export interface IMetaProps {
  item: IResult;
  publishDateLabel: string;
}

const Meta: FunctionComponent<IMetaProps> = ({ item, publishDateLabel }) => {
  const {
    pageType,
    authors,
    publishDate,
    lastReviewDate,
    readTime,
    reference,
    hasExternalUrl,
    hasCompleteOnlineUrl,
    completeOnlineUrls,
    documentType,
    watchTime,
  } = item;

  const isDocumentType = pageType === pageTypes.DOCUMENT;
  const isWebpageType = pageType === pageTypes.WEBPAGE || pageType === pageTypes.DEFAULT;
  const isArticleType = pageType === pageTypes.ARTICLE;
  const isAssetType = pageType === pageTypes.ASSET;
  const isFundEventType = pageType === pageTypes.FUNDEVENT;
  const isVideoType = pageType === pageTypes.VIDEO;
  const hasCompleteOnline = hasCompleteOnlineUrl && completeOnlineUrls;

  if (!isDocumentType && !isWebpageType && !isAssetType && !isArticleType && !isFundEventType && !isVideoType) {
    return null;
  }

  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  const formatDate = (date) => {
    const dt = new Date(date);

    return dt ? `${dt.getDate()} ${monthNames[dt.getMonth()]} ${dt.getFullYear()}` : null;
  };

  const formatReviewDate = (date) => {
    const dt = new Date(date);

    return dt ? `${monthNames[dt.getMonth()]} ${dt.getFullYear()}` : null;
  };

  const published = formatDate(publishDate) || '';
  const reviewDate = formatReviewDate(lastReviewDate) || '';

  const renderDocumentMeta = () => {
    return (
      <Fragment>
        {publishDate ? (
          <MetaItem
            name={"Published: "}
            // In cases of external URLs, the date looks similar to: "Published: Refer to document for the date", but the property name is slightly confusing. This is here intentionally.
            value={(hasExternalUrl ? publishDateLabel : published) || ''}
          />
        ) : (
          ""
        )}
        {lastReviewDate ? (
          <MetaItem
            name={"Last reviewed: "}
            value={reviewDate}
          />
        ) : (
          ""
        )}
        {reference ? (
          <MetaItem
            name={"Ref: "}
            value={reference}
          />
        ) : (
          ""
        )}
        {documentType ? (
          <MetaItem
            name={"Document type: "}
            value={documentType}
          />
        ) : (
          ""
        )}
        {hasCompleteOnline ? <CompleteOnline item={item} /> : ""}
      </Fragment>
    );
  };

  const renderWebpageMeta = () => {
    return (
      <Fragment>
        {publishDate ? (
          <MetaItem
            name={"Published: "}
            value={published}
          />
        ) : (
          ""
        )}
        {lastReviewDate ? (
          <MetaItem
            name={"Last reviewed: "}
            value={reviewDate}
          />
        ) : (
          ""
        )}
      </Fragment>
    );
  };

  const shouldRenderOldAndNewFundEvent = (type?: string | null) => {
    switch (type) {
      case fundEventTypes.CHARGESANDCOMMISSION:
      case fundEventTypes.OBJECTIVEANDPOLICYCHANGES:
      case fundEventTypes.OTHEREVENTS:
        return true;
      default:
        return false;
    }
  }

  const shouldRenderDefaultAlternativeFundEvent = (type?: string | null) => {
    switch (type) {
      case fundEventTypes.CLOSURES:
      case fundEventTypes.MERGERS:
      case fundEventTypes.NAMECHANGES:
        return true;
      default:
        return false;
    }
  }

  const shouldRenderChangeFundEvent = (type?: string | null) => {
    switch (type) {
      case fundEventTypes.OBJECTIVEANDPOLICYCHANGES:
      case fundEventTypes.OTHEREVENTS:
        return true;
      default:
        return false;
    }
  }

  const renderFundEventMeta = () => {
    return (
      <Fragment>
        <MetaItem
          name={"Event Type: "}
          value={item.fundEventType || ''}
          block={true}
        />
        <MetaItem
          name={"Companies Impacted: "}
          value={item.companiesImpacted || ''}
          block={true}
        />
        <MetaItem
          name={"Effective Date: "}
          value={formatDate(item.effectiveDate) || ''}
          block={true}
        />

        {shouldRenderOldAndNewFundEvent(item.fundEventType) &&
        (item.oldLabel || item.oldDescription) && (
          <MetaItem
            name={item.oldLabel || ''}
            value={item.oldDescription || ''}
            block={true}
          />
        )}

        {shouldRenderOldAndNewFundEvent(item.fundEventType) &&
        (item.newLabel || item.newDescription) && (
          <MetaItem
            name={item.newLabel || ''}
            value={item.newDescription || ''}
            block={true}
          />
        )}

        {shouldRenderDefaultAlternativeFundEvent(item.fundEventType) &&
        (item.defaultAlternativeFundLabel || item.defaultAlternativeFundDescription) && (
          <MetaItem
            name={item.defaultAlternativeFundLabel || ''}
            value={item.defaultAlternativeFundDescription || ''}
            block={true}
          />
        )}

        {shouldRenderChangeFundEvent(item.fundEventType) &&
        item.changeDescription && (
          <MetaItem
            name={"Change Description: "}
            value={item.changeDescription}
            block={true}
          />
        )}
      </Fragment>
    );
  };

  const renderNewsMeta = () => {
    return (
      <Fragment>
        {publishDate ? (
          <MetaItem
            name={"Published: "}
            value={published}
          />
        ) : (
          ""
        )}
        {lastReviewDate ? (
          <MetaItem
            name={"Last reviewed: "}
            value={reviewDate}
          />
        ) : (
          ""
        )}
        {authors ? (
          <MetaItem
            name={"Author: "}
            value={authors}
          />
        ) : (
          ""
        )}
        {readTime ? (
          <MetaItem
            name={`${readTime} minute read`}
            value={""}
          />
        ) : (
          ""
        )}
      </Fragment>
    );
  };

  const renderVideoMeta = () => {
    return <>
      {publishDate && <MetaItem
        name={"Published: "}
        value={published}
      />}
      {watchTime && (Number.isInteger(watchTime) && watchTime > 0) ? <MetaItem
        name={(watchTime < 60 ? `${watchTime} seconds ` : `${Math.round(watchTime / 60)} minutes `) + `watch time`}
        value={""}
      /> : null}
    </>
  }

  return (
    <div className={cx("search-results-meta", [
      {
        "search-results-meta--mobile-stacked": documentType,
      },
    ])}>
      {isDocumentType && renderDocumentMeta()}
      {isWebpageType && renderWebpageMeta()}
      {isAssetType && renderWebpageMeta()}
      {/* HELP_SUPPORT / FAQ don't have meta tags */}
      {isArticleType && renderNewsMeta()}
      {/* PERSON doesn't have meta tags */}
      {isFundEventType && renderFundEventMeta()}
      {isVideoType && renderVideoMeta()}
    </div>
  );
};

export default Meta;
